'use client';

import { BREADCRUMB_LINK_TEXT_CLASS } from '@magicschool/spellbook/components/Breadcrumb/slots';
import { useIsomorphicLayoutEffect } from '@magicschool/spellbook/hooks/useIsomorphicLayoutEffect';
import { useLatestRef } from '@magicschool/spellbook/hooks/useLatestRef';
import {
  contentInlineSize,
  reconcileObservedItems,
  SUBPIXEL_TOLERANCE,
  toPixels,
  unobserveMissingItems,
} from '@magicschool/spellbook/lib/overflowMeasurement';
import { type RefObject, useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

/** Carries a crumb's position in the full trail, which collapsing breaks in DOM order. */
const CRUMB_ATTRIBUTE = 'data-breadcrumb-crumb';
const SEPARATOR_ATTRIBUTE = 'data-breadcrumb-separator';

/** The crumb's own link, or the page crumb, which carries the link role. */
const CRUMB_LABEL_SELECTOR = 'a, [role="link"]';

/** A link crumb's text sits in the link's inner element, which is the one that ellipsizes. */
const CRUMB_TEXT_SELECTOR = `.${BREADCRUMB_LINK_TEXT_CLASS}`;

const FIRST_POSITION = 0;

/** Below three crumbs there is no middle to collapse. */
const MIN_COLLAPSIBLE_CRUMBS = 3;

/**
 * Room a collapsed trail needs to spare before it expands. An ellipsized crumb's hidden text
 * measures in whole pixels while its box is fractional, so the expanded row can read up to a pixel
 * wider than the collapsed one; with one threshold, each state would decide for the other.
 */
const EXPAND_SLACK = 1;

export type BreadcrumbOverflowState = {
  /** The middle crumbs have left the row for the overflow menu. */
  readonly collapsed: boolean;
  /** The first crumb is cut off by its ellipsis, so it needs a tooltip to be read. */
  readonly firstTruncated: boolean;
  /** The last crumb is cut off by its ellipsis, so it needs a tooltip to be read. */
  readonly lastTruncated: boolean;
};

const FITS: BreadcrumbOverflowState = { collapsed: false, firstTruncated: false, lastTruncated: false };

export type BreadcrumbLayout = {
  /** Natural width of every crumb, in trail order. */
  readonly crumbWidths: readonly number[];
  readonly separatorWidth: number;
  /** Space between the list's items. */
  readonly gap: number;
  /** The list's content width. */
  readonly available: number;
  /** Whether the middle crumbs are in the overflow menu now. */
  readonly collapsed: boolean;
};

/**
 * Whether the full trail, crumbs plus separators and gaps, is wider than the list. A collapsed
 * trail stays collapsed until it would fit with `EXPAND_SLACK` to spare.
 */
export function shouldCollapseBreadcrumbs({ crumbWidths, separatorWidth, gap, available, collapsed }: BreadcrumbLayout): boolean {
  if (crumbWidths.length < MIN_COLLAPSIBLE_CRUMBS) return false;

  const separatorCount = crumbWidths.length - 1;
  const itemCount = crumbWidths.length + separatorCount;
  const fullWidth = crumbWidths.reduce((total, width) => total + width, 0) + separatorCount * separatorWidth + (itemCount - 1) * gap;

  if (collapsed) return fullWidth > available - EXPAND_SLACK;
  return fullWidth > available + SUBPIXEL_TOLERANCE;
}

type ListMeasurements = {
  available: number;
  gap: number;
  /** Crumbs currently in the row, keyed by trail position. */
  crumbWidths: Map<number, number>;
  /** The widest separator in the row; 0 when none has painted yet. */
  separatorWidth: number;
  firstTruncated: boolean;
  lastTruncated: boolean;
};

function measureList(list: HTMLElement, lastPosition: number): ListMeasurements {
  const style = getComputedStyle(list);
  const measurements: ListMeasurements = {
    available: contentInlineSize(list, style),
    gap: toPixels(style.columnGap),
    crumbWidths: new Map(),
    separatorWidth: 0,
    firstTruncated: false,
    lastTruncated: false,
  };

  for (const item of Array.from(list.children)) {
    if (item.hasAttribute(SEPARATOR_ATTRIBUTE)) {
      // The widest, because a separator mounted by this very collapse measures 0 until its icon
      // paints. Taking that as the width makes the trail fit, expand and collapse again, in a loop.
      measurements.separatorWidth = Math.max(measurements.separatorWidth, item.getBoundingClientRect().width);
      continue;
    }

    const positionAttribute = item.getAttribute(CRUMB_ATTRIBUTE);
    if (positionAttribute === null) continue;
    const position = Number(positionAttribute);

    // An ellipsized crumb's box is narrower than its text; the text it hides is what the crumb
    // would take back with room to spare.
    const label = item.querySelector(CRUMB_LABEL_SELECTOR);
    const text = label?.querySelector(CRUMB_TEXT_SELECTOR) ?? label;
    const hiddenTextWidth = text ? Math.max(0, text.scrollWidth - text.clientWidth) : 0;
    measurements.crumbWidths.set(position, item.getBoundingClientRect().width + hiddenTextWidth);

    const truncated = hiddenTextWidth > 0;
    if (position === lastPosition) measurements.lastTruncated = truncated;
    else if (position === FIRST_POSITION) measurements.firstTruncated = truncated;
  }

  return measurements;
}

export type UseBreadcrumbOverflowResult = BreadcrumbOverflowState & {
  /** Attach to the breadcrumb list: it is both the measured container and the source of the widths. */
  listRef: RefObject<HTMLOListElement | null>;
};

/**
 * Decides whether the trail fits its list or collapses its middle crumbs into the overflow menu,
 * and whether the first or last crumb is ellipsized.
 *
 * Measurement runs before paint, so a trail that does not fit never paints wider than its list. A
 * `ResizeObserver` on the list re-runs the decision as the space changes, and every render
 * re-measures the crumbs in the row.
 *
 * Collapsed crumbs are not in the row to measure, so their widths are cached by trail position. A
 * trail whose crumbs change drops the cache and renders whole for one pass to measure again.
 *
 * @param trail One identity per crumb, in trail order; a changed entry means that crumb's width may have changed.
 */
export function useBreadcrumbOverflow(trail: readonly string[]): UseBreadcrumbOverflowResult {
  const crumbCount = trail.length;
  const trailKey = trail.join('\n');
  const listRef = useRef<HTMLOListElement>(null);
  const [state, setState] = useState<BreadcrumbOverflowState>(FITS);
  const crumbWidthsRef = useRef(new Map<number, number>());
  const measuredTrailKeyRef = useRef(trailKey);
  const latestRef = useLatestRef({ crumbCount, trailKey, state });

  const synchronize = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const { crumbCount, trailKey, state } = latestRef.current;
    const lastPosition = crumbCount - 1;
    const measurements = measureList(list, lastPosition);

    // A list with no width has not been laid out (a hidden tab, jsdom), and its crumbs measure zero too.
    if (measurements.available <= 0) return;

    const crumbWidths = crumbWidthsRef.current;
    if (measuredTrailKeyRef.current !== trailKey) {
      crumbWidths.clear();
      measuredTrailKeyRef.current = trailKey;
    }
    for (const [position, width] of measurements.crumbWidths) {
      crumbWidths.set(position, width);
    }

    const trailWidths = Array.from({ length: crumbCount }, (_, position) => crumbWidths.get(position));
    if (!trailWidths.every((width): width is number => width !== undefined)) {
      if (state.collapsed) setState(FITS);
      return;
    }

    const next: BreadcrumbOverflowState = {
      collapsed: shouldCollapseBreadcrumbs({
        crumbWidths: trailWidths,
        separatorWidth: measurements.separatorWidth,
        gap: measurements.gap,
        available: measurements.available,
        collapsed: state.collapsed,
      }),
      firstTruncated: measurements.firstTruncated,
      lastTruncated: measurements.lastTruncated,
    };

    const changed =
      next.collapsed !== state.collapsed || next.firstTruncated !== state.firstTruncated || next.lastTruncated !== state.lastTruncated;
    if (changed) setState(next);
  }, [latestRef]);

  useIsomorphicLayoutEffect(() => {
    synchronize();
  });

  // The items are watched as well as the list: an icon or a web font that loads late changes the
  // trail's width without changing the list's.
  const observerRef = useRef<ResizeObserver | null>(null);
  const observedItemsRef = useRef(new Set<Element>());

  useIsomorphicLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const observedItems = observedItemsRef.current;
    // Committing synchronously keeps the resized frame from painting the old trail, clipped. An item
    // that commit removed is dropped at once: left observed, it reports a 0 size mid-delivery.
    const observer = new ResizeObserver(() => {
      flushSync(synchronize);
      unobserveMissingItems(observer, Array.from(list.children), observedItems);
    });
    observer.observe(list);
    reconcileObservedItems(observer, Array.from(list.children), observedItems);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observedItems.clear();
      observerRef.current = null;
    };
  }, [synchronize]);

  const rowKey = `${crumbCount}:${state.collapsed}`;

  // Only a change in which items the row holds needs new observations. A frame later: this can run
  // inside the observer's own synchronous commit, where a newly observed target is reported as a
  // ResizeObserver loop error.
  useIsomorphicLayoutEffect(() => {
    const observer = observerRef.current;
    const list = listRef.current;
    if (!observer || !list) return;

    const frame = requestAnimationFrame(() => reconcileObservedItems(observer, Array.from(list.children), observedItemsRef.current));
    return () => cancelAnimationFrame(frame);
  }, [rowKey]);

  return { listRef, ...state };
}
